/**
 * Twin lot-first listing resource (D1 — "lots as the primary unit").
 *
 * Typed wrappers over `/v1/twin/lots/summary` (the produced casting lots with
 * their attribute rollup) + `/v1/twin/lots/{id}/units` (a lot's serialized
 * units, for the detail drill-in). Wire shapes mirror
 * platform-core/internal/twin/lots_summary.go exactly.
 */

import { api } from "@/lib/api-client";
import { qs } from "./qs";
import type { TrackedUnit } from "./twin-genealogy";

/** One contributing heat's slice of a produced lot — how many of the lot's
 *  units it poured, plus the heat attributes the lot inherits. */
export interface LotHeatShare {
  heat_id: string;
  heat_number?: string;
  grade?: string;
  units: number;
  pour_temp?: number;
  moisture?: number;
  carbon?: number;
}

/** A numeric attribute's min–max across a lot's contributing heats. */
export interface LotAttrRange {
  min: number;
  max: number;
}

/**
 * One produced (casting) lot with its attribute rollup — the row shape the
 * lot-first Batches surface leads with. Heat / sand / pour data are ATTRIBUTES
 * of the lot: `heats` lists each contributing heat's share ("some of the lot
 * from one heat, some from another"), and the range fields span them.
 */
export interface LotSummary {
  id: string;
  lot_number: string;
  material_class: string;
  status: string;
  /** Earliest unit of the lot (the lot's production timestamp). */
  produced_at: string;
  /** Distinct pattern ids across the lot's units. */
  patterns: string[];
  units_total: number;
  units_failed: number;
  by_defect: Record<string, number>;
  /** Heats contributing to this lot, largest share first. */
  heats: LotHeatShare[];
  grades: string[];
  pour_temp?: LotAttrRange;
  moisture?: LotAttrRange;
  carbon?: LotAttrRange;
}

export const twinLots = {
  /**
   * The lot-first listing: produced lots newest-first with the attribute
   * rollup. `material_class` overrides the produced class (defaults to the
   * org's primary part class server-side — the casting).
   */
  summaries: (params?: {
    material_class?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{
      items: LotSummary[];
      total: number;
      limit: number;
      offset: number;
    }>(`/v1/twin/lots/summary${qs(params)}`),
  /** A lot's serialized units — the drill-in table under a lot's detail. */
  units: (lotId: string, params?: { limit?: number; offset?: number }) =>
    api.get<{
      items: TrackedUnit[];
      total: number;
      limit: number;
      offset: number;
    }>(`/v1/twin/lots/${encodeURIComponent(lotId)}/units${qs(params)}`),
};
